import { FetchResult } from '@apollo/client';
import invariant from 'tiny-invariant';
import { Subscription } from 'zen-observable-ts';
import { AccountApi } from '@/api/account.api';
import { eventsApi, EVENTS } from '@/api/events.api';
import { GatewayApi } from '@/api/gateway.api';
import { CoachListStatus } from '@/constants/gateway';
import { NaluriChatError } from '@/errors';
import messagesFragment from '@/lib';
import userFragment from '@/lib/userFragement';
import {
  Channel,
  ChatChannelOptions,
  GetIndividualMessageParam,
  GetMessagesParams,
  GetMessagesResponse,
  NewMessageData,
  SendMediaMessageParams,
  SendTextMessageParams,
  SubscriptionCallback,
} from '@/types/channels';
import { ChatMessage, MessageTypes } from '@/types/messages';

import { ChatApi, chatCache } from '../api/chat.api';
import { useFragment } from '../generated';
import {
  ChatFieldsFragment,
  ChatFieldsFragmentDoc,
  ChatResultsFieldFragment,
  GetIndividualMessageDocument,
  GetIndividualMessageQuery,
  GetIndividualMessageQueryVariables,
  GetRoomChatsDocument,
  GetRoomChatsQuery,
  GetRoomChatsQueryVariables,
  GetRoomDocument,
  GetRoomQuery,
  GetRoomQueryVariables,
  GetRoomsDocument,
  GetRoomsQuery,
  GetRoomsQueryVariables,
  MarkReadDocument,
  MarkReadMutation,
  MarkReadMutationVariables,
  NewMessageDocument,
  NewMessageSubscription,
  SendMediaDocument,
  SendMediaMutation,
  SendMediaMutationVariables,
  SendTextDocument,
  SendTextMutation,
  SendTextMutationVariables,
  UserFieldsFragmentDoc,
} from '../generated/graphql';

/**
 * Naluri Chat Channel
 * @class
 */
export class ChatChannel {
  private chatClient: ChatApi;

  private gatewayClient: GatewayApi;
  private accountClient: AccountApi;

  public roomId?: string;

  private isConnected: boolean;

  private subscription?: Subscription;

  public senderId?: string;

  public lastKey: string;

  private unreadList: string[];

  private lastKeys: Map<string, string[]>;

  private emptyLastKey = '0000000-0000000';

  constructor({
    chatClient,
    gatewayClient,
    accountClient,
    roomId,
    senderId,
    lastKeys,
  }: ChatChannelOptions) {
    invariant(chatClient, 'Chat client is required');

    this.chatClient = chatClient;
    this.gatewayClient = gatewayClient;
    this.accountClient = accountClient;
    this.roomId = roomId;
    this.isConnected = false;
    this.senderId = senderId;
    this.lastKey = '';
    this.unreadList = [];
    this.lastKeys = lastKeys;
  }

  /**
   * Get channel information
   * @returns {PromiseLike<Channel>}
   * @throws NaluriChatError if error
   */
  public async getChannel(): Promise<Channel> {
    if (!this.roomId)
      throw new NaluriChatError({
        message: 'Please initialized the channel room.',
        code: '40021',
      });

    if (eventsApi.isReady())
      eventsApi.track(EVENTS.GET_CHANNEL, {
        channel: this.roomId,
      });

    const { data, error } = await this.chatClient.query<
      GetRoomQuery,
      GetRoomQueryVariables
    >({
      query: GetRoomDocument,
      variables: { id: this.roomId },
    });

    if (data?.room) {
      return {
        id: data.room.id,
        name: data.room.name,
        type: data.room.type as Channel['type'],
        lastMessageId: data.room.last_chat_id ?? undefined,
        status: data.room.status ?? undefined,
        createdAt: data.room.created_at,
        updatedAt: data.room.updated_at,
        unreadCount: data.room.unread ?? 0,
        users: useFragment(UserFieldsFragmentDoc, data.room.users).map((user) =>
          userFragment(user)
        ),
        metadata: data.room.metadata?.length
          ? data.room.metadata?.map((meta) => ({
              key: meta.key,
              value: meta.value,
            }))
          : undefined,
      };
    }

    if (eventsApi.isReady() && error) {
      eventsApi.track(EVENTS.GET_CHANNEL_ERROR, {
        channel: this.roomId,
        error,
      });
    }

    throw new NaluriChatError({
      message: `Channel getChannel method error : ${error}`,
      code: '40022',
    });
  }
  /**
   * Get messages from chat channel
   * @param {GetMessageParams} params
   * @returns {PromiseLike<GetMessagesResponse>}
   * @throws NaluriChatError if error
   */
  public async getMessages(
    { limit, after }: GetMessagesParams = { limit: 20 }
  ): Promise<GetMessagesResponse> {
    if (eventsApi.isReady())
      eventsApi.track(EVENTS.GET_CHANNEL_MESSAGES, {
        channel: this.roomId,
      });

    try {
      const { data, error } = await this.chatClient.query<
        GetRoomChatsQuery,
        GetRoomChatsQueryVariables
      >({
        query: GetRoomChatsDocument,
        variables: <GetRoomChatsQueryVariables>{
          roomId: this.roomId,
          ...(limit ? { limit } : { limit: 20 }),
          after,
        },
        fetchPolicy: 'network-only',
      });

      if (data?.chats) {
        const messagesList = data.chats.chats.length
          ? useFragment(ChatFieldsFragmentDoc, data.chats.chats).map((chat) => {
              const message = messagesFragment(chat, this.senderId);
              if (!message.readStatus) this.unreadList.push(message.id);
              return message;
            })
          : [];

        this.lastKey = data.chats.last_key ?? this.emptyLastKey;

        const currentRoomKeys = this.lastKeys.get(this.roomId);

        if (!currentRoomKeys) {
          this.lastKeys.set(this.roomId, [data.chats.last_key]);
        } else {
          // push to the top
          this.lastKeys.set(this.roomId, [
            ...new Set([data.chats.last_key, ...currentRoomKeys]),
          ]);
        }

        return {
          roomId: data.chats.room_id,
          count: data.chats.count,
          lastKey: data.chats.last_key ?? undefined,
          chats: messagesList,
        };
      }
      throw new NaluriChatError({
        message: `Channel getMessages method error : ${error}`,
        code: '40023',
      });
    } catch (error) {
      if (eventsApi.isReady())
        eventsApi.track(EVENTS.GET_CHANNEL_MESSAGES_ERROR, { error });

      throw new NaluriChatError({
        message: `Channel getMessages method error : ${error}`,
        code: '40023',
      });
    }
  }

  /**
   * Get messages from chat channel
   * @param {GetIndividualMessageParam} params
   * @returns {PromiseLiKe<ChatMessage>}
   * @throws NaluriChatError if error
   */
  public async getIndividualMessage({
    id,
    roomId,
  }: GetIndividualMessageParam): Promise<ChatMessage> {
    if (!id) {
      throw new NaluriChatError({
        message: `Channel getIndividualMessage requires message id`,
        code: '40024',
      });
    }

    if (eventsApi.isReady())
      eventsApi.track(EVENTS.GET_MESSAGE, {
        channel: this.roomId,
        // eslint-disable-next-line camelcase
        message_id: id,
      });

    const { data, error } = await this.chatClient.query<
      GetIndividualMessageQuery,
      GetIndividualMessageQueryVariables
    >({
      query: GetIndividualMessageDocument,
      variables: <GetIndividualMessageQueryVariables>{
        roomId: roomId ?? this.roomId,
        id,
      },
    });

    if (data?.chat) {
      const messagetData = messagesFragment(
        useFragment(ChatFieldsFragmentDoc, data.chat),
        this.senderId
      );

      return messagetData;
    }

    if (eventsApi.isReady() && error) {
      eventsApi.track(EVENTS.GET_MESSAGE_ERROR, {
        channel: this.roomId,
        // eslint-disable-next-line camelcase
        message_id: id,
        error,
      });
    }

    throw new NaluriChatError({
      message: `Channel getIndividualMessage method error : ${error}`,
      code: '40025',
    });
  }

  /**
   * Send text message to chat channel
   * @param {SendTextMessageParams} params
   * @returns {PromiseLike<ChatMessage>}
   * @throws NaluriChatError if error
   */
  public async sendTextMessage({
    message,
    replyTo,
  }: SendTextMessageParams): Promise<ChatMessage> {
    if (!message) {
      throw new NaluriChatError({
        message: `Channel sendTextMessage requires message`,
        code: '40025',
      });
    }

    if (eventsApi.isReady())
      eventsApi.track(EVENTS.SEND_TEXT_MESSAGE, {
        channel: this.roomId,
      });

    try {
      let metadata = [];
      metadata = await this.metadataAddActingCoach(metadata);
      const { data } = await this.chatClient.mutate<
        SendTextMutation,
        SendTextMutationVariables
      >({
        mutation: SendTextDocument,
        variables: <SendTextMutationVariables>{
          roomId: this.roomId,
          type: MessageTypes.TEXT,
          message,
          metadata,
          ...(replyTo && { replyTo: replyTo }),
        },
      });

      if (data?.sendText) {
        const sendTextData = messagesFragment(
          useFragment(ChatFieldsFragmentDoc, data.sendText),
          this.senderId
        );

        return sendTextData;
      } else {
        throw new NaluriChatError({
          message: `Channel sendTextMessage method error : missing returning data.`,
          code: '40026',
        });
      }
    } catch (err) {
      if (eventsApi.isReady())
        eventsApi.track(EVENTS.SEND_TEXT_MESSAGE_ERROR, { error: err });

      throw new NaluriChatError({
        message: `Channel sendTextMessage method error : ${err}`,
        code: '40027',
      });
    }
  }

  public async sendImageMessage({
    file,
    replyTo,
  }: SendMediaMessageParams): Promise<ChatMessage> {
    if (!file) {
      throw new NaluriChatError({
        message: `Channel sendImageMessage requires file.`,
        code: '40028',
      });
    }

    if (eventsApi.isReady())
      eventsApi.track(EVENTS.SEND_IMAGE_MESSAGE, {
        channel: this.roomId,
      });

    try {
      let metadata = [];
      metadata = await this.metadataAddActingCoach(metadata);
      const { data } = await this.chatClient.mutate<
        SendMediaMutation,
        SendMediaMutationVariables
      >({
        mutation: SendMediaDocument,
        variables: <SendMediaMutationVariables>{
          file,
          roomId: this.roomId,
          type: MessageTypes.IMAGE,
          ...(replyTo && { replyTo: replyTo }),
          metadata,
        },
        context: {
          headers: {
            'content-type': 'multipart/form-data',
          },
        },
      });

      if (data?.sendMedia) {
        const sendMediaData = messagesFragment(
          useFragment(ChatFieldsFragmentDoc, data.sendMedia),
          this.senderId
        );

        return sendMediaData;
      } else {
        throw new NaluriChatError({
          message:
            'Channel sendImageMessage method error: missing returning data',
          code: '40029',
        });
      }
    } catch (err) {
      if (eventsApi.isReady())
        eventsApi.track(EVENTS.SEND_IMAGE_MESSAGE_ERROR, { error: err });

      throw new NaluriChatError({
        message: `Channel sendImageMessage method error: ${err}`,
        code: '40030',
      });
    }
  }

  public async sendFileMessage({
    file,
    replyTo,
  }: SendMediaMessageParams): Promise<ChatMessage> {
    if (!file) {
      throw new NaluriChatError({
        message: `Channel sendFileMessage requires file.`,
        code: '40028',
      });
    }

    if (eventsApi.isReady())
      eventsApi.track(EVENTS.SEND_FILE_MESSAGE, {
        channel: this.roomId,
      });

    try {
      let metadata = [];
      metadata = await this.metadataAddActingCoach(metadata);
      const { data } = await this.chatClient.mutate<
        SendMediaMutation,
        SendMediaMutationVariables
      >({
        mutation: SendMediaDocument,
        variables: <SendMediaMutationVariables>{
          file,
          roomId: this.roomId,
          type: MessageTypes.PDF,
          ...(replyTo && { replyTo: replyTo }),
          metadata,
        },
        context: {
          headers: {
            'content-type': 'multipart/form-data',
          },
        },
      });

      if (data?.sendMedia) {
        const sendMediaData = messagesFragment(
          useFragment(ChatFieldsFragmentDoc, data.sendMedia),
          this.senderId
        );

        return sendMediaData;
      } else {
        throw new NaluriChatError({
          message:
            'Channel sendFileMessage method error: missing returning data.',
          code: '40031',
        });
      }
    } catch (err) {
      if (eventsApi.isReady())
        eventsApi.track(EVENTS.SEND_FILE_MESSAGE_ERROR, { error: err });

      throw new NaluriChatError({
        message: `Channel sendFileMessage method error: ${err}`,
        code: '40032',
      });
    }
  }

  /**
   * Marks message read, this only mark those messages that query using
   * getMessages method.
   * @returns {Void}
   * @throws NaluriChatError if error
   */
  public async markMessageRead(): Promise<void> {
    // if there's no unreadList we just return as success
    if (!this.unreadList || !this.unreadList.length) {
      return;
    }

    try {
      const promises: Promise<FetchResult<MarkReadMutation>>[] = [];

      for (const messageId of this.unreadList) {
        const promise = this.chatClient.mutate<
          MarkReadMutation,
          MarkReadMutationVariables
        >({
          mutation: MarkReadDocument,
          variables: {
            chatId: messageId,
          },
          optimisticResponse: {
            __typename: 'Mutation',
            markRead: {
              __typename: 'ChatStatus',
              // eslint-disable-next-line camelcase
              chat_id: messageId,
              status: 'READ',
            },
          },
          update: (cache, { data }) => {
            if (data?.markRead?.chat_id) {
              cache.modify({
                id: `Chat:${data.markRead.chat_id}`,
                fields: {
                  status(existingStatus = []) {
                    const newStatus = [...existingStatus];
                    newStatus.push({
                      // eslint-disable-next-line camelcase
                      chat_id: messageId,
                      status: 'READ',
                      // eslint-disable-next-line camelcase
                      user_id: this.senderId ?? '',
                    });
                    return newStatus;
                  },
                },
              });
            }
          },
        });
        promises.push(promise);
      }

      await Promise.all(promises);

      // trust me, i do not know why we need to use Rooms instead of Room
      const currentRooms = chatCache.readQuery<
        GetRoomsQuery,
        GetRoomsQueryVariables
      >({
        query: GetRoomsDocument,
        variables: {
          isChat: true,
        },
      });

      if (currentRooms?.rooms) {
        const newRooms = currentRooms.rooms.map((room) => {
          if (room.id === this.roomId) {
            return {
              ...room,
              unread: 0,
            };
          }
          return room;
        });

        chatCache.writeQuery<GetRoomsQuery, GetRoomsQueryVariables>({
          query: GetRoomsDocument,
          variables: {
            isChat: true,
          },
          data: {
            rooms: newRooms,
          },
        });
      }

      if (eventsApi.isReady())
        eventsApi.track(EVENTS.MARK_MESSAGE_AS_READ, {
          channel: this.roomId,
          // eslint-disable-next-line camelcase
          unread_count: this.unreadList.length,
        });

      this.unreadList = [];
    } catch (err) {
      throw new NaluriChatError({
        message: `Channel mark message as read error: ${err}`,
        code: '40032',
      });
    }
  }

  /**
   * subscribe method to individual channel
   * @param {SubscriptionCallback} params
   * @returns {Subscription}
   * @throws NaluriChatError if error
   */
  public subscribe(onReceive: SubscriptionCallback<ChatMessage>): Subscription {
    this.subscription = this.chatClient
      .subscribe<NewMessageSubscription>({
        query: NewMessageDocument,
        variables: {
          room: this.roomId,
        },
      })
      .subscribe({
        next: (data: NewMessageData) => {
          const newMessage = useFragment(
            ChatFieldsFragmentDoc,
            data.data.newMessage
          );

          const newMessageRef = chatCache.writeFragment<ChatFieldsFragment>({
            id: `Chat:{"id": "${newMessage.id}"}`,
            fragment: {
              ...ChatFieldsFragmentDoc,
              definitions: [
                ...ChatFieldsFragmentDoc.definitions,
                ...UserFieldsFragmentDoc.definitions,
              ],
            },
            fragmentName: 'ChatFields',
            data: newMessage,
          });

          // last key will be null if there is no previous message
          // const lastCachedKey = !this.lastKey
          //   ? this.emptyLastKey
          //   : this.lastKey;
          //   ,"last_key":"${lastCachedKey}"
          chatCache.modify({
            id: `ChatResults:{"room_id":"${this.roomId}"}`,
            fields: {
              chats(existingChats: ChatResultsFieldFragment[] = []) {
                return [newMessageRef, ...existingChats];
              },
              count(existingCount = 0) {
                return existingCount + 1;
              },
            },
          });

          const newMessageData = messagesFragment(
            useFragment(ChatFieldsFragmentDoc, data.data.newMessage),
            this.senderId
          );

          this.unreadList.push(newMessageData.id);

          onReceive({ data: newMessageData, connected: true });
        },
        error(errorValue) {
          if (eventsApi.isReady())
            eventsApi.track(EVENTS.CHANNEL_SUBSCRIPTION_ERROR, {
              error: errorValue,
            });

          onReceive({
            error: new NaluriChatError({
              message: `Channel ${errorValue}`,
              code: '40033',
            }),
            connected: false,
          });
        },
        complete() {
          onReceive({ connected: false });
        },
        start() {
          onReceive({ connected: true });
        },
      });

    return this.subscription;
  }

  public stop(): void {
    console.debug('stop');
    if (this.chatClient !== null) {
      this.chatClient.unSubscribeAll();
      this.isConnected = false;
    }
  }

  public isListening(): boolean {
    return this.isConnected;
  }

  private async metadataAddActingCoach(metadata) {
    if (this.accountClient.getUserReference() === 'naluri_coach') {
      let actingCoachId;
      try {
        if (!this.roomId) throw Error('room id not found');

        const list = await this.gatewayClient.getCoachList({
          status: CoachListStatus.ALL,
          roomId: this.roomId,
        });

        if (list.length === 0) throw Error('coach not found in list');

        actingCoachId = list.find(
          (el) => el.userId === this.accountClient.getUserId()
        )?.actingCoachId;

        metadata.push({
          key: 'actingCoachId',
          value: actingCoachId,
        });
      } catch (e) {
        if (eventsApi.isReady())
          eventsApi.track(EVENTS.CHANNEL_ACTING_COACH_INJECTION_ERROR, {
            channel: this.roomId,
            // eslint-disable-next-line camelcase
            coach_id: this.accountClient.getUserId(),
            // eslint-disable-next-line camelcase
            acting_coach_id: actingCoachId,
          });
      }
    }
    return metadata;
  }
}
